import {
  addImportantToEachRule,
  BreakpointCustomizable,
  buildSlottedStyles,
  color as colorUtils,
  FULL_TEXT_STYLES,
  getCss,
  getStateColors,
  TValidationState,
} from '../../utils';
import { getStateMessageStyles } from '../common/state-message/state-message-styles';
import {
  getBorderStateStyles,
  getInputPaddingStyles,
  getTextInputColors,
  TInputLabelPosition,
  returnTypeStyles,
  getSearchButtonStyles,
  iconPosition,
  getDateIconStyles,
  getSearchIconStyles,
} from './input-utils';

export const createStyles = (
  labelPosition: TInputLabelPosition,
  labelWidth: BreakpointCustomizable<string>,
  hideLabel: BreakpointCustomizable<boolean>,
  disabled: boolean,
  type: string,
  state: TValidationState,
  readonly: boolean,
  autocompleteMaxEntries?: number
): string => {
  const { baseLabelColor, baseInputColor, baseBorderColor } = getTextInputColors(
    state,
    disabled,
    readonly
  );
  const stateColor = disabled ? colorUtils.grey02 : getStateColors(state);
  const stateToPass = disabled ? 'none' : state;

  return getCss({
    '@global': {
      ':host': {
        display: 'block',
        fontFamily: 'Bosch Sans',
        position: 'relative',
      },
      ...returnTypeStyles(type, labelPosition, labelWidth, hideLabel, baseLabelColor),
      '::slotted': addImportantToEachRule({
        '&(input)': {
          all: 'unset',
          appearance: 'none',
          WebkitAppearance: 'none', // iOS safari
          fontFamily: 'Bosch Sans',
          fontSize: '14px',
          lineHeight: '16px',
          letterSpacing: '0.16px',
          color: baseInputColor,
          outline: `2px solid transparent`,
          outlineOffset: '-2px',
          border: 'none',
          resize: 'none',
          backgroundColor: colorUtils.white,
          ...getBorderStateStyles(stateToPass, baseBorderColor),
          ...getInputPaddingStyles(type, labelPosition, stateToPass),
        },

        '&(input:disabled)': {
          cursor: 'not-allowed',
          backgroundColor: colorUtils.white,
        },

        '&(input:hover)': {
          borderColor: stateColor || colorUtils.grey10,
        },

        '&(input:focus)': {
          outlineColor: stateColor || colorUtils.focusColor,
        },

        // needed for Safari
        '&(input:focus:not(:focus-visible))': {
          outlineColor: 'transparent',
        },

        '&(input[type="file"])': {
          display: 'none',
        },

        '&(input[type="number"])': {
          appearance: 'textfield',
        },
      }),
      'dc-ui-icon#error-icon': {
        position: 'absolute',
        ...iconPosition(labelPosition, type),
      },
      '.file-container': {
        display: 'none',
        placeContent: 'center',
        border: `1px solid ${colorUtils.grey03}`,
        padding: '10px 20px',

        '&.file-selected': {
          display: 'grid',
        },
      },
      '#search-button, #clear-search-button': {
        position: 'absolute',
        appearance: 'none',
        border: 'none',
        top: '0',
        bottom: '0',
        backgroundColor: 'transparent',
        ...getSearchButtonStyles(labelPosition),

        '&:focus-visible': {
          outline: `2px solid ${stateColor || colorUtils.focusColor}`,
          outlineOffset: '-2px',
        },
      },
      '#clear-search-button': {
        right: '0',
      },
      '#search-icon': {
        position: 'absolute',
        border: 'none',
        backgroundColor: 'transparent',
        ...getSearchIconStyles(labelPosition),
      },
      '#calendar-icon': {
        position: 'absolute',
        right: '16px',
        ...getDateIconStyles(labelPosition),
        pointerEvents: 'none',
      },
      '#input-container': {
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      },
      '.suggestions-list': {
        position: 'absolute',
        top: '56px',
        left: '0',
        width: '100%',
        backgroundColor: colorUtils.white,
        borderRadius: '0',
        boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.16)',
        zIndex: '2',
        margin: '0',
        padding: '0',
        listStyle: 'none',
        maxHeight: `${autocompleteMaxEntries * 40}px`,
        overflowY: 'auto',
      },
      '.suggestions-list mark': {
        ...FULL_TEXT_STYLES['strong-mid-size-text'].base,
        color: colorUtils.darkBlue02,
        backgroundColor: 'transparent',
      },
      '.suggestions-list li': {
        ...FULL_TEXT_STYLES['mid-size-text'].base,
        color: colorUtils.darkBlue02,
        padding: '8px 16px',
        cursor: 'pointer',

        '&:hover': {
          backgroundColor: colorUtils.grey01,
        },

        '&.active': {
          backgroundColor: colorUtils.grey01,
        },
      },
    },
    ...getStateMessageStyles(state),
  });
};

export const getSlottedCss = (host: HTMLElement, labelPosition: TInputLabelPosition): string => {
  return getCss(
    buildSlottedStyles(host, {
      // the following selectors don't work within ::slotted() pseudo selector, therefore we have to apply them via light DOM
      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button, & input[type="search"]::-webkit-search-decoration':
        {
          WebkitAppearance: 'none',
          appearance: 'none',
        },
      '& input[type="search"]::-webkit-search-cancel-button': {
        display: 'none',
      },
      '& input[type="date"]::-webkit-calendar-picker-indicator': {
        opacity: '0',
        position: 'absolute',
        right: '16px',
        ...getDateIconStyles(labelPosition),
      },
      '& input[type="text"]': {
        '&::-webkit-contacts-auto-fill-button, &::-webkit-credentials-auto-fill-button': {
          marginRight: '2.4375rem',
        },
      },
    })
  );
};
